// Power Dial — front-end state.
//
// Two zustand stores:
//   1. usePowerDialDefaults — persisted, per-agent (concurrency, redialAttempts).
//      Spec §10: "Defaults persist per-agent." Backed by localStorage so the
//      next session pre-fills the modal with the agent's last choice.
//   2. usePowerDial         — ephemeral session/UI state. The RunBar lives in
//      the app shell and reads from here.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const usePowerDialDefaults = create(
  persist(
    (set, get) => ({
      concurrency: 3,
      redialAttempts: 1,
      smartLocalId: true,
      setDefaults: (next) => set({
        concurrency: clamp(next.concurrency, 1, 5, get().concurrency),
        redialAttempts: clamp(next.redialAttempts, 1, 3, get().redialAttempts),
        smartLocalId: next.smartLocalId !== undefined ? Boolean(next.smartLocalId) : get().smartLocalId,
      }),
    }),
    { name: 'civix-powerdial-defaults' },
  ),
);

function clamp(n, lo, hi, fallback) {
  const v = Number(n);
  if (!Number.isInteger(v) || v < lo || v > hi) return fallback;
  return v;
}

export const usePowerDial = create((set, get) => ({
  // Active session — null when no session running. Hydrated on app boot via
  // /api/powerdial/sessions/active.
  session: null,
  // Lead lookup — populated from the modal so the RunBar can show names/phones
  // without a re-fetch. { [leadId]: { firstName, lastName, phone } }
  leadIndex: {},
  // Modal state
  modalOpen: false,
  modalLeadIds: [],

  setSession: (updater) => set((state) => ({
    session: typeof updater === 'function' ? updater(state.session) : updater,
  })),
  setLeadIndex: (leadIndex) => set({ leadIndex }),
  /** Add/refresh names without dropping the ones already known. */
  mergeLeadIndex: (entries) => set((state) => ({ leadIndex: { ...state.leadIndex, ...(entries || {}) } })),
  // Build the active-call descriptor for a connected power-dial lead, resolving
  // the lead's name/phone from leadIndex. Pure: reads state, returns a plain
  // object (no mutation). Name falls back to 'Lead' when the lead isn't indexed.
  // Build the active-call descriptor. `callId` is the Mongo Call._id — the
  // server resolves it from callSid and includes it in the
  // powerdial:lead:connected socket payload. Passing it through here is what
  // lets PhonePanel POST /calls/:id/disposition when the call ends; without
  // it, activeCallId stayed null and the disposition modal never opened.
  buildActiveCallFromConnected: ({ leadId, callSid, callId }) => {
    const idx = get().leadIndex[String(leadId)] || {};
    const leadName = `${idx.firstName || ''} ${idx.lastName || ''}`.trim() || 'Lead';
    return { callSid, leadId, leadName, fromNumber: idx.phone || '', callId: callId || null };
  },
  openModal: (leadIds, leadIndex = {}) => set({
    modalOpen: true,
    modalLeadIds: leadIds || [],
    leadIndex: { ...get().leadIndex, ...(leadIndex || {}) },
  }),
  // Session being continued via "Resume where I left off" (sent as resumedFrom on create).
  resumeFromSessionId: null,
  setResumeFrom: (id) => set({ resumeFromSessionId: id || null }),
  closeModal: () => set({ modalOpen: false }),
  reset: () => set({ session: null, modalOpen: false, modalLeadIds: [] }),
}));
